<template>
  <b-card-code title="Gépkezelők kezelése">
    <b-alert
      show
      variant="info"
      class="mb-2"
    >
      Gépkezelők felvétele és módosítása. Törlés csak admin jogosultsággal.
    </b-alert>

    <b-form @submit.prevent="submitForm">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Gépkezelő neve *">
            <b-form-input
              v-model="form.name"
              :disabled="isSaving || !canManage"
              placeholder="pl. Kovács Gábor"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex flex-wrap">
        <b-button
          v-if="canManage"
          variant="primary"
          type="submit"
          size="sm"
          :disabled="isSaving"
          class="mr-50 mb-50"
        >
          {{ isEdit ? (isSaving ? 'Mentés...' : 'Módosítás mentése') : (isSaving ? 'Mentés...' : 'Új gépkezelő mentése') }}
        </b-button>

        <b-button
          v-if="canManage && isEdit"
          variant="outline-secondary"
          size="sm"
          class="mb-50"
          @click="resetForm"
        >
          Mégse
        </b-button>
      </div>
    </b-form>

    <b-alert
      v-if="!canManage"
      show
      variant="warning"
      class="mt-2 mb-2"
    >
      Nincs jogosultságod a gépkezelők kezeléséhez.
    </b-alert>

    <b-table
      :items="operators"
      :fields="fields"
      small
      responsive
      striped
      class="mb-0 mt-2"
      empty-text="Nincs rögzített gépkezelő."
    >
      <template #cell(actions)="{ item }">
        <div class="d-flex align-items-center flex-wrap">
          <b-button
            v-if="canManage"
            variant="outline-primary"
            size="sm"
            class="mr-50 mb-50"
            @click="startEdit(item)"
          >
            Szerkesztés
          </b-button>

          <b-button
            v-if="isTankolasAdmin"
            variant="outline-danger"
            size="sm"
            class="mb-50"
            @click="deleteItem(item)"
          >
            Törlés
          </b-button>
        </div>
      </template>
    </b-table>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { checkRole } from '@/middleware/authLoader'
import axios from 'axios'
import {
  BAlert,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BTable,
} from 'bootstrap-vue'

export default {
  name: 'TankolasOperators',
  components: {
    BCardCode,
    BAlert,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BTable,
  },
  data() {
    return {
      isSaving: false,
      editId: null,
      fields: [
        { key: 'name', label: 'Gépkezelő neve' },
        { key: 'actions', label: 'Művelet' },
      ],
      operators: [],
      form: {
        name: '',
      },
    }
  },
  computed: {
    isTankolasAdmin() {
      return checkRole('tankolas_role_admin')
    },
    canManage() {
      return checkRole('tankolas_role_halado') || checkRole('tankolas_role_admin')
    },
    isEdit() {
      return !!this.editId
    },
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    toastError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Mentés sikertelen.',
          icon: 'XOctagonIcon',
          variant: 'danger',
        },
      })
    },
    toastSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Mentve.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
    extractBackendError(error) {
      if (!error || !error.response || !error.response.data) return 'Mentés sikertelen.'
      const data = error.response.data
      if (data.message) return data.message
      if (data.errors && typeof data.errors === 'object') {
        const firstField = Object.keys(data.errors)[0]
        if (firstField && Array.isArray(data.errors[firstField]) && data.errors[firstField][0]) {
          return data.errors[firstField][0]
        }
      }
      return 'Mentés sikertelen.'
    },
    validateForm() {
      if (!this.form.name || !this.form.name.trim()) return 'A gépkezelő neve kötelező.'
      return null
    },
    async loadItems() {
      try {
        const response = await axios({
          method: 'get',
          url: 'tankolas/operators',
          headers: this.authHeaders(),
        })

        this.operators = Array.isArray(response.data.items) ? response.data.items : []
      } catch (error) {
        this.toastError('A gépkezelő lista nem tölthető be.')
      }
    },
    resetForm() {
      this.editId = null
      this.form.name = ''
    },
    startEdit(item) {
      this.editId = item.id
      this.form.name = item.name || ''
    },
    async submitForm() {
      if (!this.canManage) {
        this.toastError('Nincs jogosultságod a gépkezelők kezeléséhez.')
        return
      }

      const validationError = this.validateForm()
      if (validationError) {
        this.toastError(validationError)
        return
      }

      this.isSaving = true
      try {
        const isEdit = this.isEdit
        const response = await axios({
          method: isEdit ? 'put' : 'post',
          url: isEdit ? `tankolas/operators/${this.editId}` : 'tankolas/operators',
          headers: this.authHeaders(),
          data: {
            name: this.form.name,
          },
        })

        this.toastSuccess(response.data.message || 'Mentve.')
        this.resetForm()
        await this.loadItems()
      } catch (error) {
        this.toastError(this.extractBackendError(error))
      } finally {
        this.isSaving = false
      }
    },
    async deleteItem(item) {
      if (!this.isTankolasAdmin) return
      if (!window.confirm(`Biztosan törlöd ezt a gépkezelőt: ${item.name}?`)) return

      try {
        const response = await axios({
          method: 'delete',
          url: `tankolas/operators/${item.id}`,
          headers: this.authHeaders(),
        })

        this.toastSuccess(response.data.message || 'Törölve.')
        await this.loadItems()
      } catch (error) {
        this.toastError(this.extractBackendError(error))
      }
    },
  },
}
</script>
